import { consentLogger } from './consentLogger';
import { withWindow } from './withWindow';

/**
 * Polaris consent categories used in data-polaris-consent attributes
 */
export type ConsentCategory = 'essential' | 'statistics' | 'marketing';

/**
 * Consent state for all categories
 */
export type ConsentState = Readonly<{
  essential: boolean;
  statistics: boolean;
  marketing: boolean;
}>;

/**
 * Default consent state when consent is not required (e.g., US users where
 * the Polaris banner variant is NONE). All categories are granted because
 * no explicit consent decision is needed.
 */
const CONSENT_NOT_REQUIRED_STATE: ConsentState = {
  essential: true,
  statistics: true,
  marketing: true,
};

/**
 * Default consent state when consent is required but has not yet been granted
 * (e.g., EEA/UK users who haven't interacted with the banner yet), or when
 * the consent state cannot be determined.
 */
const CONSENT_DENIED_STATE: ConsentState = {
  essential: true,
  statistics: false,
  marketing: false,
};

/**
 * Polaris cookie name prefixes
 * Polaris consent cookies can have various prefixes depending on the version
 */
const POLARIS_COOKIE_PREFIXES = ['polaris_consent=', '_polaris_consent=', 'truevault_polaris='] as const;

/**
 * Result of attempting to parse the Polaris consent cookie.
 *
 * - `absent`: No consent cookie was found in document.cookie
 * - `valid`: Cookie was found and successfully parsed
 * - `invalid`: Cookie was found but could not be decoded or parsed (fail closed)
 */
type ConsentCookieResult = { status: 'absent' } | { status: 'valid'; data: Record<string, unknown> } | { status: 'invalid' };

/**
 * Parse the Polaris consent cookie from document.cookie
 *
 * Splits document.cookie, finds the Polaris consent cookie by known prefixes,
 * decodes and JSON-parses it once.
 *
 * @returns A discriminated result indicating whether the cookie was absent, valid, or invalid
 */
function parsePolarisConsentCookie(): ConsentCookieResult {
  const cookies = document.cookie.split(';').map((c) => c.trim());

  const polarisConsentCookie = cookies.find((c) => POLARIS_COOKIE_PREFIXES.some((prefix) => c.startsWith(prefix)));

  if (!polarisConsentCookie) {
    return { status: 'absent' };
  }

  try {
    const cookieValue = polarisConsentCookie.substring(polarisConsentCookie.indexOf('=') + 1);
    const decoded = decodeURIComponent(cookieValue);

    try {
      return { status: 'valid', data: JSON.parse(decoded) };
    } catch {
      consentLogger.warn('Consent cookie is not valid JSON:', decoded);
      return { status: 'invalid' };
    }
  } catch (e) {
    consentLogger.warn('Failed to parse consent cookie:', e);
    return { status: 'invalid' };
  }
}

/**
 * Options for getConsentState that provide additional CMP context
 */
export type ConsentStateOptions = Readonly<{
  /**
   * Whether the consent banner was shown to the user.
   *
   * - `true`: Banner was/is displayed — if no cookie exists, deny consent (conservative)
   * - `false`: Banner was NOT displayed (e.g., US users with NONE variant) — if no cookie
   *   exists, grant all consent since no explicit decision is required
   * - `undefined`: Unknown — fall back to conservative deny (backwards compatible)
   */
  bannerShown?: boolean;
}>;

/**
 * Get consent state for all categories
 *
 * Parses the consent cookie once and builds the full state from the single result.
 * When no cookie exists, uses the `bannerShown` option to determine whether consent
 * was required:
 * - If bannerShown is false, no consent decision was required → grant all consent
 * - Otherwise, conservatively deny non-essential consent
 *
 * When the cookie exists but is malformed, we fail closed (deny non-essential consent)
 * regardless of the bannerShown flag.
 *
 * @param options - Optional CMP context for smarter defaults when no cookie exists
 * @returns The current consent state for all categories
 */
export function getConsentState(options?: ConsentStateOptions): ConsentState {
  return withWindow(() => {
    const cookieResult = parsePolarisConsentCookie();

    // If a consent cookie exists and is valid, use its values
    if (cookieResult.status === 'valid') {
      return {
        essential: true,
        statistics: Boolean(cookieResult.data['statistics']),
        marketing: Boolean(cookieResult.data['marketing']),
      };
    }

    // If the cookie exists but is malformed, fail closed — deny non-essential consent.
    // We don't grant implicit consent when we can't trust the cookie data.
    if (cookieResult.status === 'invalid') {
      consentLogger.warn('Malformed consent cookie — denying non-essential consent');
      return CONSENT_DENIED_STATE;
    }

    // No consent cookie found (absent).
    // If we know no banner was shown (e.g., US users where Polaris variant is NONE),
    // consent is not required — grant all categories implicitly.
    if (options?.bannerShown === false) {
      consentLogger.log('No consent cookie and no banner shown — granting implicit consent');
      return CONSENT_NOT_REQUIRED_STATE;
    }

    // Default: no cookie and banner was shown (or unknown) → deny (conservative)
    return CONSENT_DENIED_STATE;
  }, CONSENT_DENIED_STATE);
}

/**
 * Determine whether the consent banner was shown to the user based on GPP ping data.
 *
 * Only returns `false` (banner not shown) when the CMP explicitly reports the banner
 * as 'hidden' or 'disabled'. Returns `true` (conservative — assume banner was shown)
 * when the status is 'visible' or when the status is unknown/undefined, to avoid
 * accidentally granting implicit consent.
 *
 * @param pingData - GPP ping response data
 * @returns true if the banner was/is displayed or status is unknown, false only when
 *          the CMP explicitly reports the banner was not shown
 */
function wasBannerShown(pingData: Pick<GppPingData, 'cmpDisplayStatus'>): boolean {
  // If the CMP explicitly reports the banner as visible, it was shown
  if (pingData.cmpDisplayStatus === 'visible') {
    return true;
  }

  // For 'hidden' or 'disabled': the banner was either never shown (NONE variant)
  // or was shown and then dismissed (user interacted). In the latter case, a consent
  // cookie would exist, so getConsentState will use cookie values anyway.
  // When no cookie exists + hidden/disabled → banner was never shown.
  if (pingData.cmpDisplayStatus === 'hidden' || pingData.cmpDisplayStatus === 'disabled') {
    return false;
  }

  // Unknown or undefined cmpDisplayStatus — conservatively assume the banner was shown
  // to avoid accidentally granting implicit consent
  consentLogger.warn('Unknown cmpDisplayStatus:', pingData.cmpDisplayStatus, '— assuming banner was shown');
  return true;
}

/**
 * Wait for Polaris consent to be determined, then call callback with consent state
 *
 * This function listens for the GPP useractioncomplete event to detect when the user
 * has made their consent choices. It also checks if consent has already been determined.
 *
 * The callback is guaranteed to be invoked exactly once, even if consent is already
 * determined when this function is called.
 *
 * @param callback - Function to call when consent is ready, receives the consent state
 * @returns Cleanup function to remove event listeners (call when component unmounts)
 *
 * @example
 * ```typescript
 * const cleanup = onConsentReady((consent) => {
 *   console.log('Consent state:', consent);
 *   initializeAnalytics(consent);
 * });
 *
 * // Later, when cleaning up:
 * cleanup();
 * ```
 */
export function onConsentReady(callback: (consent: ConsentState) => void): () => void {
  return withWindow(
    (global) => {
      // Guard flag to ensure callback only fires once
      let hasCalledBack = false;
      let eventListenerCallback: ((event: { eventName: string }) => void) | undefined;

      const safeCallback = (consent: ConsentState) => {
        if (hasCalledBack) return;
        hasCalledBack = true;
        callback(consent);
      };

      if (global.__gpp) {
        // Check if consent already determined
        global.__gpp('ping', (pingData) => {
          consentLogger.log('Checking consent state:', pingData);

          if (pingData.cmpStatus === 'loaded' && pingData.signalStatus === 'ready') {
            // Determine if the consent banner was shown to decide the default
            // when no consent cookie exists (US/non-EEA vs EEA/UK users)
            const bannerShown = wasBannerShown(pingData);
            const consent = getConsentState({ bannerShown });
            consentLogger.log('Consent already determined:', consent, '(bannerShown:', bannerShown, ')');
            safeCallback(consent);
          }
        });

        // Also listen for new consent events
        eventListenerCallback = (event) => {
          if (event.eventName === 'useractioncomplete') {
            // User explicitly interacted with the banner — cookie should now be set
            const consent = getConsentState({ bannerShown: true });
            consentLogger.log('User action complete, consent state:', consent);
            safeCallback(consent);
          }
        };

        global.__gpp('addEventListener', eventListenerCallback, 'useractioncomplete');

        // Return cleanup function
        return () => {
          if (global.__gpp && eventListenerCallback) {
            global.__gpp('removeEventListener', eventListenerCallback, 'useractioncomplete');
          }
        };
      } else {
        consentLogger.warn('GPP API not available');
        // Fallback: assume no consent
        safeCallback(CONSENT_DENIED_STATE);
        return () => {}; // No cleanup needed
      }
    },
    () => {}
  );
}
